import React, { useState } from "react";
import axios from "axios";
import { Button, OutlineButton, SubtleButton } from "components/button";
import GroupedButton from "components/button/grouped";
import Field from "components/field";
import Select from "react-select";
import { useModalContext } from "components/modal";
import ClientSelect from "components/react-select/clients";
import { useSnackbarContext } from "components/snackbar";
import { useFormik } from "formik";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { TCustomer } from "types/customer";
import { TProperty, TPropertyForm, TPropertyFormSubmit } from "types/property";
import * as Yup from "yup";
import { NewPropertyMapContainer } from "./NewPropertyMapContainer";
import { TTag, TTagOption } from "types/tag";
import Sort from "components/sort";
import { geoLocatePermanentAddress } from "./api/geoLocateAddressApi";
import { useTranslation } from 'react-i18next';

type Props = {
  customerId?: string;
  property?: TProperty;
  customerInfo?: TCustomer;
  notModal?: boolean;
  onSubmit?: () => void;
  onCancel?: () => void;
  returnedPropertyId?: (property: TProperty) => void;
};
type TPropertyTaxOption = {
  value: string;
  label: string;
  optionType: "tax" | "group";
  id: number;
  tax_type?: string;
  sortLabel?: string;
};
type TDefaultTaxFilter = "all" | "single" | "combined";

export default function NewPropertyModal(props: Props) {
  const { t } = useTranslation();
  const tBase = "views.properties.newPropertyModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const modal = useModalContext();
  const snackbar = useSnackbarContext();
  const queryClient = useQueryClient();

  const [showConfirmGps, setShowConfirmGps] = useState(false);
  const [pendingValues, setPendingValues] = useState<TPropertyForm | null>(null);

  const tagsQuery = useQuery<Array<TTag>>("tags", async () =>
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/tags/customer&property`)
      .then((res) => res.data)
  );

  const tagOptions = React.useMemo((): TTagOption[] => {
    return tagsQuery.data?.map((tag) => ({ value: tag.id, label: tag.label })) || [];
  }, [tagsQuery.data]);

  const taxOptionsQuery = useQuery(
    "propertyTaxOptions",
    async () =>
      await axios
        .get(
          `
      ${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/both
      `
        )
        .then((res) => res.data)
  );
  

  const [sortedTags, setSortedTags] = useState(tagOptions);
  const [sortedDefaultTaxOptions, setSortedDefaultTaxOptions] = useState<TPropertyTaxOption[]>([]);
  const [defaultTaxFilter, setDefaultTaxFilter] = useState<TDefaultTaxFilter>("all");

  const defaultTaxOptions = React.useMemo(() => {
    if (!taxOptionsQuery.data) return [];

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });
    const byLabel = (a: TPropertyTaxOption, b: TPropertyTaxOption) =>
      collator.compare(a.sortLabel ?? a.label ?? "", b.sortLabel ?? b.label ?? "");

    const taxes =
      taxOptionsQuery.data?.taxes
        ?.filter((tax: { tax_type?: string }) => tax.tax_type === "tax")
        .map((tax: any) => ({
          ...tax,
          value: `tax:${tax.id}`,
          optionType: "tax" as const,
          sortLabel: tax.label,
        })) || [];

    const combinedTaxGroups =
      taxOptionsQuery.data?.taxGroups
        ?.filter(
          (group: any) =>
            Array.isArray(group.taxes) &&
            group.taxes.length > 0 &&
            group.taxes.every((tax: any) => tax.tax_type === "tax")
        )
        .map((group: any) => ({
          ...group,
          value: `group:${group.id}`,
          optionType: "group" as const,
          label: `${group.label} (${tr("Combined Tax Rate")})`,
          sortLabel: group.label,
        })) || [];

    return [...taxes].sort(byLabel).concat([...combinedTaxGroups].sort(byLabel));
  }, [taxOptionsQuery.data]);

  React.useEffect(() => {
    setSortedDefaultTaxOptions(defaultTaxOptions);
  }, [defaultTaxOptions]);

  const mutation = useMutation(
    async (e: TPropertyFormSubmit) =>
      await axios
        .post(
          `
    ${process.env.REACT_APP_SERVER_URL}/api/v1/company/property/${props.property?.id}
    `,
          {
            ...e,
            address: addressObj.address,
            isStandardizedAddress: addressObj.isStandardizedAddress,
          }
        )
        .then((res) => {props.returnedPropertyId && props.returnedPropertyId(res.data); return res.data}),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries();
        !props.notModal && modal.setModal();
        snackbar.showSnackbar(
          `${tr("Property")} ${props.property ? tr("modified!") : tr("created!")}`
        );
        props.onSubmit && props.onSubmit();
        props.returnedPropertyId && props.returnedPropertyId(res);
      },
    }
  );

  const initialValues: TPropertyForm = {
    address: props.property?.address || "",
    city: props.property?.city || "",
    state: props.property?.state || "",
    name: props.property?.name || "",
    zipcode: props.property?.zipcode || "",
    zip_ext: props.property?.zip_ext || "",
    owner: props.property?.owner
      ? { ...props.property.owner, id: props.property.owner.id.toString() }
      : props.customerInfo || null,
    billed_to: props.property?.billed_to
      ? {
          ...props.property.billed_to,
          id: props.property.billed_to.id.toString(),
        }
      : props.customerInfo || null,
    default_tax: props.property?.default_tax || null,
    default_tax_group: props.property?.default_tax_group || null,
    latitude: props.property?.latitude || null,
    longitude: props.property?.longitude || null,
    tags: props.property?.tags?.map(tag => ({
      value: tag.id,
      label: tag.label,
    })) || [],
  };

  const validationSchema = Yup.object({
    address: Yup.string().required(),
    city: Yup.string().required(),
    state: Yup.string().required().length(2),
    zipcode: Yup.string().required().length(5),
    zip_ext: Yup.string().nullable().transform((v) => v === "" ? null : v).length(4),
    name: Yup.string(),
    owner: Yup.object().nullable(),
    billed_to: Yup.object().nullable(),
    latitude: Yup.number().nullable(),
    longitude: Yup.number().nullable(),
    tags: Yup.array().nullable(),
  });
  
  const onSubmit = async (e: TPropertyForm) => {
    const isAddressChanged =
      !!props.property &&
      (e.address !== (props.property?.address || "") ||
      e.city !== (props.property?.city || "") ||
      e.state !== (props.property?.state || "") ||
      e.zipcode !== (props.property?.zipcode || ""));

    const hasNoGps = !e.latitude || !e.longitude;

    if (hasNoGps) {
      const tags = e.tags ? e.tags.map((tag) => tag.value) : [];
      let lat = e.latitude;
      let lng = e.longitude;
      const addressStr = encodeURIComponent(`${e.address} ${e.city} ${e.state}, ${e.zipcode}`);
      try {
        const geoLocateResponse = await geoLocatePermanentAddress(addressStr);
        if (geoLocateResponse && geoLocateResponse.length >= 2) {
          lng = geoLocateResponse[0];
          lat = geoLocateResponse[1];
        }
      } catch (err) {
        console.error("Failed to automatically geolocate address:", err);
      }
      mutation.mutate({
        ...e,
        tags,
        latitude: lat,
        longitude: lng,
      });
    } else if (isAddressChanged) {
      setPendingValues(e);
      setShowConfirmGps(true);
    } else {
      // 3. Normal save
      const tags = e.tags ? e.tags.map((tag) => tag.value) : [];
      mutation.mutate({ ...e, tags });
    }
  };

  const formik = useFormik<TPropertyForm>({
    initialValues,
    onSubmit,
    validationSchema,
  });

  React.useEffect(() => {
    const autoGeocodeOnLoad = async () => {
      if (!formik.values.latitude || !formik.values.longitude) {
        const { address, city, state, zipcode } = formik.values;
        if (address && city && state && zipcode) {
          const addressStr = encodeURIComponent(`${address} ${city} ${state}, ${zipcode}`);
          try {
            const geoLocateResponse = await geoLocatePermanentAddress(addressStr);
            if (geoLocateResponse && geoLocateResponse.length >= 2) {
              formik.setFieldValue("longitude", geoLocateResponse[0]);
              formik.setFieldValue("latitude", geoLocateResponse[1]);
            }
          } catch (err) {
            console.error("Failed to automatically geolocate on load:", err);
          }
        }
      }
    };
    autoGeocodeOnLoad();
  }, []);


  const [addressObj, setAddressObj] = React.useState({
    address: formik.values.address || "",
    isStandardizedAddress: false,
    selectedOption: {
      description: formik.values.address || "",
      city: formik.values.city || "",
      state: formik.values.state || "",
      zipcode: formik.values.zipcode || "",
      zip_ext: formik.values.zip_ext || "",
    },
  });

  const selectedDefaultTaxValue = React.useMemo(() => {
    if (formik.values.default_tax_group) {
      return `group:${formik.values.default_tax_group}`;
    }
    if (formik.values.default_tax) {
      return `tax:${formik.values.default_tax}`;
    }
    return null;
  }, [formik.values.default_tax, formik.values.default_tax_group]);

  const selectedDefaultTaxOption = React.useMemo(
    () =>
      sortedDefaultTaxOptions.find(
        (option: TPropertyTaxOption) => option.value === selectedDefaultTaxValue
      ) || null,
    [sortedDefaultTaxOptions, selectedDefaultTaxValue]
  );

  const filteredDefaultTaxOptions = React.useMemo(() => {
    if (defaultTaxFilter === "single") {
      return sortedDefaultTaxOptions.filter((option) => option.optionType === "tax");
    }
    if (defaultTaxFilter === "combined") {
      return sortedDefaultTaxOptions.filter((option) => option.optionType === "group");
    }
    return sortedDefaultTaxOptions;
  }, [defaultTaxFilter, sortedDefaultTaxOptions]);

  return (
    <div className="relative">
      {showConfirmGps && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 -m-6 rounded-lg">
          <div className="bg-white p-6 rounded-lg shadow-2xl max-w-md w-full border border-gray-100 mx-4 text-center">
            <h3 className="text-lg font-medium text-gray-800 mb-6">
              {tr("addressChangedGps")}
            </h3>
            <div className="flex justify-end space-x-4">
              <SubtleButton
                type="button"
                style={{ margin: '10px' }}
                onClick={() => {
                  setShowConfirmGps(false);
                  if (pendingValues) {
                    const tags = pendingValues.tags ? pendingValues.tags.map((tag) => tag.value) : [];
                    mutation.mutate({ ...pendingValues, tags });
                  }
                }}
              >
                {tr("No")}
              </SubtleButton>
              <Button
                primary
                type="button"
                style={{ margin: '10px' }}
                onClick={async () => {
                  setShowConfirmGps(false);
                  if (pendingValues) {
                    const tags = pendingValues.tags ? pendingValues.tags.map((tag) => tag.value) : [];
                    let lat = pendingValues.latitude;
                    let lng = pendingValues.longitude;
                    const addressStr = encodeURIComponent(`${pendingValues.address} ${pendingValues.city} ${pendingValues.state}, ${pendingValues.zipcode}`);
                    try {
                      const geoLocateResponse = await geoLocatePermanentAddress(addressStr);
                      if (geoLocateResponse && geoLocateResponse.length >= 2) {
                        lng = geoLocateResponse[0];
                        lat = geoLocateResponse[1];
                      }
                    } catch (err) {
                      console.error("Failed to geolocate address:", err);
                    }
                    mutation.mutate({
                      ...pendingValues,
                      tags,
                      latitude: lat,
                      longitude: lng,
                    });
                  }
                }}
              >
                {tr("Yes")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={formik.handleSubmit} className="space-y-4 max-w-[800px]">
        {/* Address Input */}
        {/* <StandardizedAddressInput {...{ setAddressObj, addressObj }} label={tr("Address")} formik={formik} required /> */}
        <Field
          name="address"
          required
          label={tr("Address")}
          value={addressObj.address || ""}
          onChange={(e: any) =>
            {
              const value = e.target.value;
              setAddressObj({
                ...addressObj,
                isStandardizedAddress: false,
                selectedOption: { ...addressObj.selectedOption, description: value },
                address: value,
              });
              formik.setFieldValue("address", value);
            }
          }
          error={formik.touched.address && formik.errors.address ? String(formik.errors.address) : ""}
        />
    
        {/* City, State, Zipcode */}
        <div className="flex space-x-2">
          <Field
            name="city"
            className="w-[35%]"
            required
            label={tr("City")}
            value={addressObj.selectedOption.city || ""}
            onChange={(e: any) =>
              {
                const value = e.target.value;
                setAddressObj({
                  ...addressObj,
                  isStandardizedAddress: false,
                  selectedOption: { ...addressObj.selectedOption, city: value },
                });
                formik.setFieldValue("city", value);
              }
            }
            error={formik.touched.city && formik.errors.city ? String(formik.errors.city) : ""}
          />
          <Field
            name="state"
            className="w-[20%]"
            required
            label={tr("State")}
            value={addressObj.selectedOption.state || ""}
            onChange={(e: any) =>
              {
                const value = e.target.value;
                setAddressObj({
                  ...addressObj,
                  isStandardizedAddress: false,
                  selectedOption: { ...addressObj.selectedOption, state: value },
                });
                formik.setFieldValue("state", value);
              }
            }
            error={formik.touched.state && formik.errors.state ? String(formik.errors.state) : ""}
          />

            <Field
              name="zipcode"
              className="w-[20%]"
              required
              label={tr("Zipcode")}
              value={addressObj.selectedOption.zipcode || ""}
              onChange={(e: any) =>
                {
                  const value = e.target.value;
                  setAddressObj({
                    ...addressObj,
                    isStandardizedAddress: false,
                    selectedOption: { ...addressObj.selectedOption, zipcode: value },
                  });
                  formik.setFieldValue("zipcode", value);
                }
              }
              maxLength={5}
              error={formik.touched.zipcode && formik.errors.zipcode ? String(formik.errors.zipcode) : ""}
            />
            <div className="bg-black h-[1px] w-[1%] mt-[42px]" />
            <Field
              name="zip_ext"
              className="w-[15%]"
              label="Ext"
              value={addressObj.selectedOption.zip_ext || ""}
              onChange={(e: any) =>
                {
                  const value = e.target.value;
                  setAddressObj({
                    ...addressObj,
                    isStandardizedAddress: false,
                    selectedOption: { ...addressObj.selectedOption, zip_ext: value },
                  });
                  formik.setFieldValue("zip_ext", value);
                }
              }
              maxLength={4}
              error={formik.touched.zip_ext && formik.errors.zip_ext ? String(formik.errors.zip_ext) : ""}
            />

        </div>
    
        {/* Nickname */}
        <Field name="name" formik={formik} label={tr("Nickname")} />
    
        {/* Owner and Billed To */}
        <div className="flex space-x-2">
          <ClientSelect
            error={formik.touched.owner && formik.errors.owner ? String(formik.errors.owner) : ""}
            isClearable
            value={
              formik.values.owner
                ? { ...formik.values.owner, value: formik.values.owner.id, label: formik.values.owner.name }
                : null
            }
            label={tr("Owner")}
            onChange={(e) => formik.setValues((v) => ({ ...v, owner: e }))}
          />
          <ClientSelect
            error={formik.touched.billed_to && formik.errors.billed_to ? String(formik.errors.billed_to) : ""}
            isClearable
            value={
              formik.values.billed_to
                ? { ...formik.values.billed_to, value: formik.values.billed_to.id, label: formik.values.billed_to.name }
                : null
            }
            label={tr("Billed To")}
            onChange={(e) => formik.setValues((v) => ({ ...v, billed_to: e }))}
          />
        </div>
    
        {/* Default Tax */}
        <div>
          <div className="text-[12.8px]">{tr("Default Tax")}</div>
          <div className="mb-1 flex items-center justify-start space-x-4">
            <Sort
              data={defaultTaxOptions}
              sortBy="sortLabel"
              onSorted={setSortedDefaultTaxOptions}
            />
            <GroupedButton className="flex">
              <OutlineButton
                size="small"
                $selected={defaultTaxFilter === "all"}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setDefaultTaxFilter("all")}
              >
                {tr("All")}
              </OutlineButton>
              <OutlineButton
                size="small"
                $selected={defaultTaxFilter === "single"}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setDefaultTaxFilter("single")}
              >
                {tr("Single")}
              </OutlineButton>
              <OutlineButton
                size="small"
                $selected={defaultTaxFilter === "combined"}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setDefaultTaxFilter("combined")}
              >
                {tr("Combined")}
              </OutlineButton>
            </GroupedButton>
          </div>
          <Field
            type="select"
            style={{ width: "100%" }}
            options={filteredDefaultTaxOptions}
            menuPortalTarget={null}
            menuPosition="absolute"
            name="default_tax"
            placeholder={tr("Select a tax")}
            value={selectedDefaultTaxOption}
            isClearable
            onChange={(option: TPropertyTaxOption | null) => {
              if (!option) {
                formik.setFieldValue("default_tax", null);
                formik.setFieldValue("default_tax_group", null);
                return;
              }
              if (option.optionType === "group") {
                formik.setFieldValue("default_tax", null);
                formik.setFieldValue("default_tax_group", option.id);
                return;
              }
              formik.setFieldValue("default_tax", option.id);
              formik.setFieldValue("default_tax_group", null);
            }}
          />
        </div>
    
        {/* Tags */}
        <div>
          <div className="text-[12.8px]">{tr("Tags")}</div>
          <Sort data={tagOptions} sortBy="label" onSorted={setSortedTags} className="mb-1" />
          <Select<TTagOption, true>
            isMulti
            name="tags"
            options={sortedTags}
            className="basic-multi-select"
            label={tr("Tags")}
            classNamePrefix="select"
            value={formik.values.tags}
            onChange={(selectedOptions) => {
              const tags: TTagOption[] = selectedOptions
                ? selectedOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))
                : [];
              formik.setFieldValue("tags", tags);
            }}
            placeholder={tr("Select tags...")}
          />
        </div>
    
        {/* Map */}
        <div>
          <div className="text-sm">{tr("Map")}</div>
          <NewPropertyMapContainer formik={formik} />
        </div>
    
        {/* Buttons */}
        <div className="flex justify-end space-x-2">
          <OutlineButton
            type="button"
            onClick={() => {
              if (!props.notModal) {
                modal.setModal();
              } else if (props.onCancel) {
                props.onCancel();
              }
            }}
          >
            {tr("Cancel")}
          </OutlineButton>
          <Button 
            primary 
            type="submit" 
            disabled={
              formik.isSubmitting ||
              !formik.isValid || 
              (!formik.dirty && !!formik.values.latitude && !!formik.values.longitude) || 
              mutation.isLoading
            }
          >
            {tr("Save")}
          </Button>
        </div>
      </form>
    </div>
  );
}